import { splitIntoWeeks } from "common";
import { Dayjs } from "dayjs";
import { useEffect, useMemo } from "react";
import { useInfiniteCollection } from "./useInfiniteCollection";
import { getTimeslotParams } from "@/components/Dashboard/Common/timeslotParams";
import { DashboardScope } from "@/types/Dashboard";

export const useWeeklyTimeslotCollections = (
    startParam: Dayjs,
    endParam: Dayjs,
    isMonthMode: boolean,
    filterScope: DashboardScope,
) => {
    // We will always have weeks length is equal to 6
    // and there will be no error with a different number of hooks when rerendering the component.
    const weeks = useMemo(() => getWeeksInRange(startParam, endParam), [startParam, endParam]);
    const isDasboardScopeAll = filterScope === DashboardScope.ALL && isMonthMode;
    const results = weeks.map(({ start, end }, index) => {
        const monthParams = start ? getTimeslotParams()(start, end, { limit: 500 }) : {};
        const paramsForOtherModes = start ? getTimeslotParams()(startParam, endParam, { limit: 500 }) : {};
        const isEnabled = isMonthMode
            ? start !== null && end !== null
            : start !== null && !startParam.isAfter(endParam);
        const params = isDasboardScopeAll
            ? splitIntoWeeks(startParam, endParam).map((range) =>
                  getTimeslotParams()(range.start, range.end, { limit: 500 }),
              )
            : isMonthMode
            ? monthParams
            : paramsForOtherModes;

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const response = useInfiniteCollection("timeslots", params, {
            queryKey: ["timeslots", "collection", "infinite", index, params],
            queryItemLimit: isDasboardScopeAll ? 1500 : 500,
            staleTime: 5000,
            enabled: !(isDasboardScopeAll && index !== 0) && isEnabled,
        });
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            if (response.hasNextPage && !response.isFetchingNextPage && !response.isFetching) {
                response.fetchNextPage();
            }
        }, [response]);

        return response;
    });

    return results;
};

const getWeeksInRange = (startDate: Dayjs, endDate: Dayjs) => {
    const weeks: Array<{ start: Dayjs | null; end: Dayjs | null }> = Array.from({ length: 6 }, () => ({
        start: null,
        end: null,
    }));

    let currentStart = startDate.startOf("week");
    let weekIndex = 0;

    while (currentStart.isBefore(endDate) && weekIndex < 6) {
        const currentEnd = currentStart.endOf("week");

        weeks[weekIndex] = { start: currentStart, end: currentEnd };

        currentStart = currentStart.add(1, "week");
        weekIndex++;
    }

    return weeks;
};
